import React from "react";
import classNames from "classnames";
import { capitalize, pluralize } from "@common/helpers/string";
import { MatchedTransactionsCard } from "@components/transactions/MatchedTransactionCard/MatchedTransactionCard";
import shellStyles from "@components/transactions/MatchedTransactionCard/MatchCardShell.module.scss";
import { Tooltip } from "@design-system/Tooltip/Tooltip";
import { Button } from "@design-system/Button/Button";
import { ClickableContainer } from "@design-system/ClickableContainer/ClickableContainer";
import { HStack } from "@design-system/HStack/HStack";
import { Loader } from "@design-system/Loader/Loader";
import { PanelEmptyMessage } from "@design-system/PanelEmptyMessage/PanelEmptyMessage";
import { VStack } from "@design-system/VStack/VStack";
import { SearchIcon } from "@icons";
import styles from "./AccrualMatchedTransactionsSection.module.scss";
import { ImportedSettlementsCard } from "./ImportedSettlementsCard";
import {
    AccrualMatchDocument,
    useAccrualMatchData,
} from "./useAccrualMatchData";

/**
 * @purpose Matches body of a bill details panel accordion. Shown for every
 * entity; `AccrualDetailsPanel` decides whether this section's accordion item
 * exists at all.
 */
export const AccrualMatchedTransactionsSection: React.FC<
    AccrualMatchDocument
> = (props) => {
    const { documentType } = props;
    const {
        workspaceId,
        hasError,
        isRetrying,
        isLoading,
        retry,
        settlements,
        rows,
        handleOpenReverseDrawer,
    } = useAccrualMatchData(props);

    // Both match queries are disabled until the workspace scope resolves, so
    // the query state says nothing about this document yet.
    if (!workspaceId) {
        return null;
    }

    if (hasError) {
        return (
            <VStack gap={8} align="center" padding={16}>
                <PanelEmptyMessage>Could not load matches</PanelEmptyMessage>
                <Button
                    variant="primary"
                    size="sm"
                    onClick={retry}
                    disabled={isRetrying}
                >
                    Retry
                </Button>
            </VStack>
        );
    }

    // The accordion panel only mounts this section once the user expands it, so
    // the request starts while they are already looking at the panel — claiming
    // "no matches yet" before it resolves would be wrong.
    if (isLoading) {
        return (
            <HStack justify="center" padding={16}>
                <Loader />
            </HStack>
        );
    }

    const settlementsCard = (
        <ImportedSettlementsCard
            documentType={documentType}
            settlements={settlements}
        />
    );

    if (rows.length === 0) {
        return (
            <>
                {settlementsCard}
                <div className={styles.findMatch}>
                    <Tooltip
                        content={`Match this ${documentType} to one or more transactions`}
                    >
                        <div>
                            <HStack
                                as={ClickableContainer}
                                onClick={handleOpenReverseDrawer}
                                justify="between"
                                align="center"
                                className={classNames(
                                    "mb-0 cursor-pointer",
                                    shellStyles.control,
                                )}
                            >
                                <span className={styles.findMatchLabel}>
                                    Find a match
                                </span>
                                <div
                                    className={styles.matchIcon}
                                    aria-label="Find a match"
                                >
                                    <SearchIcon />
                                </div>
                            </HStack>
                        </div>
                    </Tooltip>
                </div>
            </>
        );
    }

    return (
        <>
            <MatchedTransactionsCard
                label={capitalize(pluralize("match", rows.length))}
                rows={rows}
                onHeaderClick={handleOpenReverseDrawer}
                headerIcon={
                    <div className={styles.headerIcon} aria-label="Match">
                        <SearchIcon />
                    </div>
                }
            />
            {settlementsCard}
        </>
    );
};
